'use client';
import React, { useMemo, useState, useCallback, ReactElement } from 'react';

import useVisibilityObserver from '@storefront/hooks/useVisibilityObserver';
import { getLogger } from '@storefront/logging';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import Script from 'next/script';
import { env } from 'next-runtime-env';

import VirtualAgent from '../virtual-agent/virtual-agent';
import VirtualAgentContext, {
  VirtualAgentType,
} from '../virtual-agent/virtualAgentContext';
import { VirtualAgentSkeleton } from '../virtual-agent-skeleton/virtual-agent-skeleton';

export interface VirtualAgentWrapperProps {
  isMobile?: boolean;
  margin?: boolean;
  pageName?: string;
  onClickAction?: () => unknown;
  appearance?: VirtualAgentType;
  description?: ReactElement;
  chatWithUs?: boolean;
  defaultColor?: boolean;
  controlVisibility?: boolean;
  analyticsScreenName?: string;
}

const ONE_SECOND_IN_MS = 1000;

export function VirtualAgentWrapper({
  appearance = 'balloon',
  controlVisibility = false,
  description,
  pageName,
  chatWithUs,
  defaultColor,
  analyticsScreenName,
  ...props
}: Readonly<VirtualAgentWrapperProps>) {
  const { onClickAction } = props;

  const [chatEnabled, setChatEnabled] = useState(true);
  const [scriptEnabled, setScriptEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paperEventSent, setPaperEventSent] = useState(false);
  const [hasInterface, setHasInterface] = useState(false);

  const isFooterVisible = useVisibilityObserver(
    'footer-container',
    0.1
  )?.isVisible;

  const isChooseYourPagePlanVisible = useVisibilityObserver(
    'options-modal-container',
    0.1
  )?.isVisible;

  const isAddPaperVisible = useVisibilityObserver(
    'add-paper-section',
    0.1
  )?.isVisible;

  const isSummaryPage = pageName === 'summary';

  function readyChatCallback(): boolean {
    setChatEnabled(true);
    setHasInterface(true);
    setLoading(false);
    return true;
  }

  const openChatPane = (attempt = 0): NodeJS.Timeout | void => {
    if (attempt > 10) {
      setChatEnabled(false);
      setLoading(false);
      return;
    }
    const insideFrontInterface = window.insideFrontInterface;
    if (!insideFrontInterface?.chatReady) {
      setChatEnabled(false);
      return setTimeout(() => openChatPane(attempt + 1), ONE_SECOND_IN_MS);
    }
    insideFrontInterface.openChatPane(true, readyChatCallback);
  };

  const shouldShowChatButton = controlVisibility ? !isFooterVisible : true;

  const getChatScreenMode = useCallback((): string => {
    if (isAddPaperVisible) {
      return 'AddPaper';
    }
    if (isChooseYourPagePlanVisible) {
      return 'ChooseYourPagePlan';
    }

    if (isSummaryPage) {
      return '';
    }

    return 'ChooseYourPrinter';
  }, [isAddPaperVisible, isChooseYourPagePlanVisible, isSummaryPage]);

  const handleOnClickAction = async () => {
    setScriptEnabled(true);
    setLoading(true);
    openChatPane();

    if (onClickAction) {
      onClickAction();
      return;
    }

    try {
      const screenMode = getChatScreenMode();
      const screenName = isSummaryPage ? 'SummaryPage' : analyticsScreenName;
      let eventData;

      if (screenMode === 'AddPaper' && !paperEventSent) {
        eventData = {
          ...uiEvents.addPaperChatBC,
          screenName,
          screenMode,
        };
        await DataCollection.sendEvent(eventData);
        setPaperEventSent(true);
      } else if (screenMode !== 'AddPaper') {
        eventData = {
          ...uiEvents.printerConfigurationChoosePrinterBC,
          screenName,
          screenMode,
        };
        await DataCollection.sendEvent(eventData);
      }
    } catch (error) {
      console.error('Error sending event:', error);
    }
  };

  const logger = getLogger('virtual-agent-wrapper');
  const powerfrontURL = env('NEXT_PUBLIC_POWERFRONT_URL');

  return (
    <VirtualAgentContext.Provider
      value={useMemo(
        () => ({
          chatEnabled,
          setChatEnabled,
          appearance,
          chatWithUs,
          defaultColor,
        }),
        [chatEnabled, setChatEnabled, appearance, chatWithUs, defaultColor]
      )}
    >
      {loading && !hasInterface && (
        <VirtualAgentSkeleton handleClose={() => setLoading(false)} />
      )}
      {scriptEnabled && (
        <Script
          src={powerfrontURL}
          id="powerfront-script"
          onReady={() => {
            logger.info(`Powerfront Loaded: ${powerfrontURL}`);
          }}
        />
      )}
      {shouldShowChatButton && (
        <VirtualAgent
          handleOnClickAction={handleOnClickAction}
          description={description}
        />
      )}
    </VirtualAgentContext.Provider>
  );
}

export default VirtualAgentWrapper;
